/**
 * Platform exams — the published datesheets, and how firm they are.
 *
 * These apply to a COHORT rather than to a class, which is what stops every
 * class re-entering a national timetable and lets a student with no class have
 * real dates at all. Until now they could only be seeded by script, so nobody
 * could see what existed or notice when a projected date needed replacing with
 * an announced one. That is the question this page answers.
 *
 * See docs/FeatureDesigns/goals-syllabus-revision-plan/one-preparation-model-target.md §2.1.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../api/adminApi';
import type { PlatformExam } from '../api/platformExamApi';
import { DataTable, type ColumnDef } from '@shared-ui/components';

const fmt = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });

/** Days from today, UTC on both sides so a timezone cannot shift the count. */
const daysAway = (iso: string) => Math.round(
  (Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`)) / 86_400_000,
);

interface Row {
  exam: PlatformExam;
  first?: string;
  last?: string;
}

export function PlatformExams() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-exams'],
    queryFn: () => adminApi.listPlatformExams(),
  });

  const rows: Row[] = useMemo(() => {
    const exams = data?.exams ?? [];
    return exams
      .map((exam) => {
        const dates = exam.papers.map((p) => p.date).sort();
        return { exam, first: dates[0], last: dates[dates.length - 1] };
      })
      // Soonest first: the calendar that needs attention is the one about to
      // matter, not the one alphabetically first.
      .sort((a, b) => (a.first ?? '9999').localeCompare(b.first ?? '9999'));
  }, [data]);

  const columns: ColumnDef<Row>[] = [
    {
      key: 'cohort', header: 'Cohort', mobile: 'title',
      render: (r) => {
        const c = r.exam.cohort;
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{r.exam.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              {c ? `${c.board}${c.grade ? ` · Class ${c.grade}` : ''} · ${c.year}` : r.exam.ownerId}
            </div>
          </div>
        );
      },
    },
    {
      // The reason this page exists: a projected calendar is OUR estimate from
      // the recurring window and needs replacing when the board announces.
      key: 'status', header: 'Dates', mobile: 'accessory',
      render: (r) => (r.exam.status === 'official'
        ? <span style={{ color: 'var(--color-success)' }}>official</span>
        : <span style={{ color: 'var(--color-warning)' }} title="Our estimate from the recurring window — replace when the board announces">projected</span>),
    },
    {
      key: 'papers', header: 'Papers', mobile: 'hide',
      render: (r) => `${r.exam.papers.length}`,
    },
    {
      key: 'window', header: 'Window', mobileLabel: 'When',
      render: (r) => (r.first
        ? (
          <div>
            <div>{fmt(r.first)}{r.last && r.last !== r.first ? ` – ${fmt(r.last)}` : ''}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              {daysAway(r.first) >= 0 ? `in ${daysAway(r.first)} days` : 'past'}
            </div>
          </div>
        )
        : <span style={{ color: 'var(--text-tertiary)' }}>no papers</span>),
    },
    {
      key: 'subjects', header: 'Subjects', mobile: 'hide',
      render: (r) => {
        const subs = [...new Set(r.exam.papers.flatMap((p) => p.subjects))];
        return <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{subs.join(', ') || '—'}</span>;
      },
    },
  ];

  return (
    <div className="settings-section">
      <h2 style={{ marginBottom: 4 }}>Exams</h2>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0, maxWidth: 720 }}>
        Published datesheets, applying to a whole cohort rather than to a class — so no class
        re-enters a national timetable, and a student in no class still has real dates.
        A <strong>projected</strong> calendar is our estimate from the recurring window;
        replace it with the board&apos;s own when it is announced.
      </p>

      {error && <div style={{ color: 'var(--color-error)' }}>{(error as Error).message}</div>}

      {!isLoading && rows.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          No datesheets seeded yet. Students setting an exam goal will be asked to type a date
          until one exists. Seed them with
          <code style={{ margin: '0 4px' }}>scripts/seed_platform_exams.cjs</code>.
        </p>
      )}

      <DataTable columns={columns} data={rows} rowKey={(r) => r.exam.id} loading={isLoading} />
    </div>
  );
}
